import _ from 'lodash'
import { encodeLanguages } from '@/views/creative/utils/i18n.js'
import { Base64 } from 'js-base64'
import {
  generateGameConfigResource,
  generateI18nResource,
  isCommonI18n,
} from './index'
import i18n from '@/lang'
import {
  cookGameConfig,
  addMappingPathToGameConfig,
  hasCustomGameConfigInGameConfigTab,
  customGameConfigKeysInGameConfigTab,
  customGameConfigKeysOutsideOfGameConfigTab,
} from './game-config'
import {
  gameConfigCategoryResourceType,
  getGameDifficultyCategoryIdByTag,
  getGameDifficultyCategoryPrefixByTag,
  i18nCategoryResourceType,
} from '@/store/creative/common/category'

/**
 * 生产 gameConfigResource
 * @param gameConfig {{}}
 * @param category {{}}
 * @param isMultiCustom {boolean}
 * @param dcoConfig {{}}
 * @returns {{}}
 */
function useGameConfigToProductGameConfigResource({
  gameConfig,
  category,
  isMultiCustom,
  dcoConfig,
  hasInitGameConfigCategory,
}) {
  gameConfig = _.cloneDeep(gameConfig)

  // 如果没有通用的配置，则不需要加工多个难度模式
  const hasCommonGameConfig = hasCustomGameConfigInGameConfigTab({
    categoryList: category,
    hasInitGameConfigCategory,
    gameConfig,
  })

  let gameDifficultyList = null
  if (hasCommonGameConfig) {
    if (_.isPlainObject(dcoConfig) && _.toArray(dcoConfig).length > 0) {
      gameDifficultyList = _.toArray(dcoConfig).map((item) => {
        return {
          tag: item.tag,
          is_default: item.is_default,
          ...generateGameConfigResource({
            category_id: getGameDifficultyCategoryIdByTag(item.tag),
            name: i18n.t(`playTurbo.gamePlay.difficulty.${item.tag}.name`),
            prefix: getGameDifficultyCategoryPrefixByTag(item.tag),
            data: _.pick(
              item.value.gameConfig,
              customGameConfigKeysInGameConfigTab({
                categoryList: category,
                hasInitGameConfigCategory,
                gameConfig,
              }),
            ), // 游戏配置/object
          }),
        }
      })
    } else {
      gameDifficultyList = [
        {
          ...generateGameConfigResource({
            category_id: getGameDifficultyCategoryIdByTag('easy'),
            name: i18n.t(`playTurbo.gamePlay.difficulty.easy.name`),
            prefix: getGameDifficultyCategoryPrefixByTag('easy'),
            data: _.pick(
              gameConfig,
              customGameConfigKeysInGameConfigTab({
                categoryList: category,
                hasInitGameConfigCategory,
                gameConfig,
              }),
            ), // 游戏配置/object
          }),
          tag: 'easy', // 标签/string
          is_default: true, // 是否默认/boolean
        },
      ]
    }
  }

  // 提取不在通用玩法 tab 下的配置
  const customGameConfigOutsideOfGameConfigTab = _.pick(
    gameConfig,
    customGameConfigKeysOutsideOfGameConfigTab({
      categoryList: category,
      hasInitGameConfigCategory,
      gameConfig,
    }),
  )

  const gameConfigResource = cookGameConfigResource({
    originalGameConfig: customGameConfigOutsideOfGameConfigTab,
    category,
    isMultiCustom,
  })

  if (_.isArray(gameDifficultyList)) {
    gameConfigResource.gameDifficultyList = gameDifficultyList
  }

  return gameConfigResource
}

function cookI18nResource({ i18n, category, isMultiCustom }) {
  const { languages, fallbackLocale } = i18n
  const i18nResource = {}
  const prefixList = []

  const rec = (list) => {
    _.each(list, (item) => {
      if (Array.isArray(item.sub_category)) {
        rec(item.sub_category)
        return
      }

      // 找到不在语言分类下的 i18n（挂靠在特定 resource/group/stage 下的），并组装数据
      if (item.resource_type === i18nCategoryResourceType) {
        if (!languages[fallbackLocale]) {
          throw new Error('fallback locale is illegal')
        }

        const cook = ({ textKey, prefix }) => {
          const data = {}
          // 找到该属性对应的所有语言的配置
          _.each(languages, (lang, key) => {
            if (lang[textKey]) {
              data[key] = lang[textKey]
            }
          })

          i18nResource[textKey] = [
            generateI18nResource({
              category_id: item.id,
              name: item.name,
              name_en: item.name_en || item.name,
              prefix,
              data,
            }),
          ]
        }

        const properties = item.properties ? JSON.parse(item.properties) : null
        const refKeys = _.get(properties, 'refKeys')
          ? [..._.get(properties, 'refKeys')]
          : [item.prefix]

        // 过滤已处理过的 prefix
        if (refKeys.every((item) => prefixList.includes(item))) {
          return
        }

        _.each(refKeys, (refKey) => {
          const encodePrefix = Base64.encode(refKey)
          const current = languages[fallbackLocale][encodePrefix]
          // 兜底语言有对应属性，且至少有 resource/stage/group/scene 其中一个属性
          if (current && !isCommonI18n(current)) {
            cook({
              textKey: encodePrefix,
              prefix: refKey,
            })
          }

          // 竖屏
          const refKey_V = `${refKey}_V`
          const encodePrefix_V = Base64.encode(`${refKey}_V`)
          const current_V = languages[fallbackLocale][encodePrefix_V]
          if (current_V && !isCommonI18n(current_V)) {
            cook({
              textKey: encodePrefix_V,
              prefix: refKey_V,
            })
          }

          // 横屏
          const refKey_H = `${refKey}_H`
          const encodePrefix_H = Base64.encode(`${refKey}_H`)
          const current_H = languages[fallbackLocale][encodePrefix_H]
          if (current_H && !isCommonI18n(current_H)) {
            cook({
              textKey: encodePrefix_H,
              prefix: refKey_H,
            })
          }
          prefixList.push(refKey)
        })
      }
    })
  }

  rec(category)
  return i18nResource
}

function cookGameConfigResource({
  originalGameConfig,
  category,
  isMultiCustom,
}) {
  const gameConfigResource = {}
  const rec = (list) => {
    _.each(list, (item) => {
      if (Array.isArray(item.sub_category)) {
        rec(item.sub_category)
        return
      }

      const prefix = item.prefix
      // 找到不在通用玩法下的 gameConfig（挂靠在特定 resource/group/stage 下的），并组装数据
      if (
        item.resource_type === gameConfigCategoryResourceType &&
        originalGameConfig[prefix]
      ) {
        gameConfigResource[prefix] = [
          generateGameConfigResource({
            category_id: item.id,
            name: item.name,
            prefix: prefix,
            data: _.cloneDeep(originalGameConfig[prefix]),
          }),
        ]
      }
    })
  }

  rec(category)
  return gameConfigResource
}

function i18nValidator({ languages, fallbackLocale }) {
  const languageKeys = Object.keys(languages)
  let valid = true

  // 校验配置中的 resource/stage/group 属性的值与兜底语言配置的是否一致
  _.each(languages[fallbackLocale], (item, key) => {
    if (
      !languageKeys.every((j) => {
        return languages[j][key].resource === item.resource
      })
    ) {
      console.error(`i18n config error, ${key} 没有 resource 属性`)
      valid = false
    }

    if (
      !languageKeys.every((j) => {
        return languages[j][key].stage === item.stage
      })
    ) {
      console.error(`i18n config error, ${key} 没有 stage 属性`)
      valid = false
    }

    if (
      !languageKeys.every((j) => {
        return languages[j][key].group === item.group
      })
    ) {
      console.error(`i18n config error, ${key} 没有 group 属性`)
      valid = false
    }

    if (
      !languageKeys.every((j) => {
        return languages[j][key].scene === item.scene
      })
    ) {
      console.error(`i18n config error, ${key} 没有 group 属性`)
      valid = false
    }
  })

  return valid
}

/**
 * 加工 json config，需要针对不同的制作类型区别处理
 * @param jsonConfig {{}}
 * @param dcoConfig {{}}
 * @param editorConfig {{}|null}
 * @param gameDifficulty {string}
 * @param page {string}
 * @param isDco {boolean}
 * @param category {{}[]}
 * @param isMultiCustom {boolean}
 * @param presetConfig {{}}
 * @returns {{jsonConfig: {}, gameConfigResource: {}}}
 */
function cookJsonConfig({
  jsonConfig,
  dcoConfig,
  gameDifficulty,
  page,
  isDco,
  editorConfig,
  category,
  isMultiCustom,
  hasInitGameConfigCategory,
  presetConfig,
}) {
  const cloneJsonConfig = _.cloneDeep(jsonConfig)
  let gameConfigResource = null
  let i18nResource = null

  // 代码字
  const originalLanguages = _.get(cloneJsonConfig, 'i18n.languages')
  const fallbackLocale = _.get(cloneJsonConfig, 'i18n.fallbackLocale')
  const languages = Object.keys(originalLanguages || {})
  const getI18nResource = () => {
    const encodedLanguages = encodeLanguages(originalLanguages)
    _.set(cloneJsonConfig, 'i18n.languages', encodedLanguages)
    // 多组合制作且有i18nResource
    if (_.get(editorConfig, 'i18nResource')) {
      return _.get(editorConfig, 'i18nResource')
    }
    return cookI18nResource({
      i18n: _.get(cloneJsonConfig, 'i18n'),
      category,
      isMultiCustom,
    })
  }
  if (originalLanguages && fallbackLocale) {
    if (i18nValidator({ languages: originalLanguages, fallbackLocale })) {
      i18nResource = getI18nResource()
    }
  }

  // 玩法
  const gameConfig = cookGameConfig(cloneJsonConfig.gameConfig)
  const getGameConfigResource = () => {
    return useGameConfigToProductGameConfigResource({
      gameConfig,
      category,
      isMultiCustom,
      hasInitGameConfigCategory,
    })
  }
  // v3 版游戏配置才需要特殊处理
  if (gameConfig && gameDifficulty === 'v3') {
    gameConfigResource = getGameConfigResource()
  }

  // 为 GameConfigResource 添加 mapping_path 属性，便于在 json_config 中找回配置来源
  const addMappingPathToGameConfigResource = () => {
    _.each(
      gameConfigResource,
      (gameConfigResourceItem, gameConfigResourceKey) => {
        if (gameConfigResourceKey === 'gameDifficultyList') {
          _.each(
            gameConfigResourceItem,
            (gameDifficultyItem, gameDifficultyItemIndex) => {
              _.each(gameDifficultyItem.data, (item, key) => {
                // const path = `[${gameConfigResourceKey}][${gameDifficultyItemIndex}][data][${key}]`
                addMappingPathToGameConfig({
                  config: item,
                  mappingPath: `[gameConfig][${key}]`,
                })
              })
            },
          )
        } else {
          _.each(gameConfigResourceItem, (item, index) => {
            // const path = `[${gameConfigResourceKey}][${index}][data]`
            addMappingPathToGameConfig({
              config: item.data,
              mappingPath: `[gameConfig][${gameConfigResourceKey}]`,
            })
          })
        }
      },
    )
  }
  addMappingPathToGameConfigResource()

  // presetConfig
  if (!_.isEmpty(presetConfig)) {
    _.set(cloneJsonConfig, 'presetConfig', presetConfig)
  }

  return {
    jsonConfig: cloneJsonConfig,
    gameConfigResource,
    i18nResource,
    languages,
    // 代码字兜底语言
    currentLanguage: _.get(cloneJsonConfig, 'i18n.fallbackLocale'),
  }
}

export default cookJsonConfig
